/* @flow */
import { combineReducers } from 'redux-immutable';
import { createRootEpic } from 'trustar/utils/redux-utils';
import { reducer as form } from 'redux-form/immutable'; // from npm module
import { routerReducer as router } from 'react-router-redux';
import { Map } from 'immutable';

/* Store and Epic Imports */
import app, { epic as appEpic } from 'modules/app';
import alerts, { epic as alertEpic } from 'modules/alerts';
import chat, { epic as chatEpic } from 'modules/chat';
import companies, { epic as companiesEpic } from 'modules/companies';
import create, { epic as createEpic } from 'modules/create';
import dataFeeds, { epic as dataFeedsEpic } from 'modules/data-feeds';
import devtasks, { epic as devtasksEpic } from 'modules/devtasks';
import enclaves, { epic as enclavesEpic } from 'modules/enclave';
import enclaveManagement, {
  epic as enclaveManagementEpic
} from 'modules/enclave-management';
import { epic as iconsEpic } from 'modules/icons';
import miscellaneous, {
  epic as miscellaneousEpic
} from 'modules/miscellaneous';
import templates, { epic as templatesEpic } from 'modules/templates';
import subscriptionManagement, {
  epic as subscriptionManagementEpic
} from 'modules/subscription-management';
import subscriptions, {
  epic as subscriptionsEpic
} from 'modules/subscriptions';
import groups, { epic as sharingGroupsEpic } from 'modules/groups';
import insights, { epic as insightsEpic } from './insights';
import marketplace, { epic as marketplaceEpic } from 'modules/marketplace';
import malware, { epic as malwareEpic } from 'modules/malware';
import threatActors, { epic as threatActorsEpic } from 'modules/threat-actors';
import metrics, { epic as metricsEpic } from 'modules/metrics';
import profile, { epic as profileEpic } from 'modules/profile';
import redaction, { epic as redactionEpic } from 'modules/redaction';
import reports, { epic as reportsEpic } from 'modules/reports';
import summaries, { epic as summariesEpic } from 'modules/summaries';
import entityScores, { epic as entityScoresEpic } from 'modules/entity-scores';
import errors, { epic as errorsEpic } from 'modules/errors';
import display from 'modules/display';
import roles, { epic as rolesEpic } from 'modules/roles';
import sectors, { epic as sectorsEpic } from 'modules/sectors';
import settings from 'modules/settings';
import users, { epic as usersEpic } from 'modules/users';
import indicators, { epic as indicatorsEpic } from 'modules/indicators';
import globalWhitelist, {
  epic as globalWhitelistEpic
} from 'modules/global-whitelist';
import companyWhitelist, {
  epic as companyWhitelistEpic
} from 'modules/company-whitelist';
import search, { epic as searchEpic } from 'modules/search';
import { epic as settingsEpic } from 'modules/settings';
import tags, { epic as tagsEpic } from 'modules/tags';
import triage, { epic as triageEpic } from 'modules/triage';
import notificationPreferences, {
  epic as notificationPreferencesEpic
} from 'modules/notificationPreferences';
import navigationEpic from 'modules/router/epics.js';
/* These moduels are still oddballs because they both export two store reducers...
 * We will resolve this later. Should just be a matter of updating getters */
import {
  stores as analysisStores,
  epic as analysisEpic
} from 'modules/analysis';
import {
  stores as constellationStores,
  epic as constellationEpic
} from 'modules/constellation';
import { epic as trackingEpic } from 'modules/tracking';
import features, { epic as featuresEpic } from 'modules/features';

/* Epics:
 *
 * NOTE: We give these a name key so the error wrapper which is applied by createRootEpic
 * can report the offending epic for error reporting if an uncaught error propagation occurs
 * in production. Some of these stacktraces previously have not contained enough information
 * to diagnose the error source. Providing the name should help with this.
 *
 ================================================================================= */
export const rootEpic = createRootEpic([
  { epic: entityScoresEpic, name: 'entityScoresEpic' },
  { epic: globalWhitelistEpic, name: 'globalWhitelistEpic' },
  { epic: companyWhitelistEpic, name: 'companyWhitelistEpic' },
  { epic: alertEpic, name: 'alertEpic' },
  { epic: appEpic, name: 'appEpic' },
  { epic: chatEpic, name: 'chatEpic' },
  { epic: companiesEpic, name: 'companiesEpic' },
  { epic: constellationEpic, name: 'constellationEpic' },
  { epic: analysisEpic, name: 'analysisEpic' },
  { epic: createEpic, name: 'createEpic' },
  { epic: dataFeedsEpic, name: 'dataFeedsEpic' },
  { epic: errorsEpic, name: 'errorsEpic' },
  { epic: devtasksEpic, name: 'devtasksEpic' },
  { epic: enclavesEpic, name: 'enclavesEpic' },
  { epic: enclaveManagementEpic, name: 'enclaveManagementEpic' },
  { epic: iconsEpic, name: 'iconsEpic' },
  { epic: miscellaneousEpic, name: 'miscellaneousEpic' },
  { epic: templatesEpic, name: 'templatesEpic' },
  { epic: subscriptionsEpic, name: 'subscriptionsEpic' },
  { epic: subscriptionManagementEpic, name: 'subscriptionManagementEpic' },
  { epic: sharingGroupsEpic, name: 'groupsEpic' },
  { epic: marketplaceEpic, name: 'marketplaceEpic' },
  { epic: insightsEpic, name: 'insightsEpic' },
  { epic: malwareEpic, name: 'malwareEpic' },
  { epic: threatActorsEpic, name: 'threatActorsEpic' },
  { epic: metricsEpic, name: 'metricsEpic' },
  { epic: profileEpic, name: 'profileEpic' },
  { epic: redactionEpic, name: 'redactionEpic' },
  { epic: reportsEpic, name: 'reportsEpic' },
  { epic: rolesEpic, name: 'rolesEpic' },
  { epic: searchEpic, name: 'searchEpic' },
  { epic: sectorsEpic, name: 'sectorsEpic' },
  { epic: usersEpic, name: 'usersEpic' },
  { epic: settingsEpic, name: 'settingsEpic' },
  { epic: tagsEpic, name: 'tagsEpic' },
  { epic: navigationEpic, name: 'navigationEpic' },
  { epic: indicatorsEpic, name: 'indicatorsEpic' },
  { epic: trackingEpic, name: 'trackingEpic' },
  { epic: notificationPreferencesEpic, name: 'notificationPreferencesEpic' },
  { epic: featuresEpic, name: 'featuresEpic' },
  { epic: summariesEpic, name: 'summariesEpic' },
  { epic: triageEpic, name: 'triageEpic' }
]);

/* Stores:
 ================================================================================= */
export const rootReducer = combineReducers({
  entityScores,
  globalWhitelist,
  companyWhitelist,
  form,
  router,
  app,
  alerts,
  chat,
  companies,
  create,
  dataFeeds,
  devtasks,
  enclaves,
  enclaveManagement,
  miscellaneous,
  templates,
  subscriptions,
  subscriptionManagement,
  groups,
  insights,
  marketplace,
  malware,
  threatActors,
  metrics,
  profile,
  redaction,
  reports,
  display,
  roles,
  sectors,
  users,
  features,
  errors,
  // analytics,
  search,
  settings,
  tags,
  indicators,
  summaries,
  notificationPreferences,
  analyses: analysisStores.analyses,
  analysesMeta: analysisStores.analysesMeta,
  constellation: constellationStores.constellation,
  constellationMeta: constellationStores.constellationMeta,
  triage,
});

/* Serializers:
 * Used to serialize state to plain JS so that it can be stored in local
 * storage. NOTE: There is no need to call JSON.stringify, that will be handled
 * by redux-persist
 ================================================================================= */
type Serializer = (state: Map<any, any>) => { [key: string]: any };

const combineSerializers = (serializers: { [key: string]: Serializer }) => {
  const combined = (state: Map<any, any>, key: string) => {
    const serializer = serializers[key];

    if (typeof serializer !== 'function') {
      console.warn(
        `WARNING: Tried to serialize state for key "${key}" but you did not provide a serializer.`
      );
      return state;
    }

    return serializers[key](state);
  };

  // NOTE: This might seem odd, it is. I'm storing the keys here so that when we
  // create the store whitelist in redux-persist we don't need to duplicate the
  // store keys. This way whenever we update our rootSerializer below with new
  // stores they will be included in redux persist.
  combined.keys = Object.keys(serializers);

  return combined;
};

import { serialize as serializeApp } from 'modules/app/stores.js';
import { serialize as serializeEnclaves } from 'modules/enclave/stores.js';
import { serialize as serializeSeenUpsellModal } from 'modules/profile/stores.js';
import { serialize as serializeSettings } from 'modules/settings/stores.js';

export const rootSerializer = combineSerializers({
  app: serializeApp,
  enclaves: serializeEnclaves,
  profile: serializeSeenUpsellModal,
  settings: serializeSettings
});



// WEBPACK FOOTER //
// ./src/app/modules/index.js